import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  ArrowLeft, Crown, Check, CreditCard, Calendar, Users, 
  Car, AlertTriangle, Loader2, RefreshCw, XCircle
} from "lucide-react";

export default function SubscriptionsPage() {
  const [subscription, setSubscription] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscription();
  }, []);

  const fetchSubscription = async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get("/subscriptions/my");
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to fetch subscription:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm("Are you sure you want to cancel your subscription? You will lose access to the dispatch center.")) {
      return;
    }

    setIsCancelling(true);
    try {
      await apiClient.post("/subscriptions/cancel");
      toast.success("Subscription cancelled successfully");
      setSubscription(null);
      // Redirect to pricing since they need a subscription
      setTimeout(() => navigate("/pricing"), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setIsCancelling(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric"
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-slate-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate("/employer")}
            className="mb-4"
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
          
          <h1 className="text-3xl font-bold text-slate-900 mb-2">
            Subscription Management
          </h1>
          <p className="text-slate-600">
            Manage your subscription plan and billing
          </p>
        </div>

        {subscription ? (
          <>
            {/* Active Subscription Card */}
            <Card className="mb-6 border-green-200 bg-gradient-to-r from-green-50 to-emerald-50" data-testid="active-subscription-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                      <Crown className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-green-800">
                        {subscription.plan_name} Plan
                      </CardTitle>
                      <CardDescription className="text-green-600">
                        Active Subscription
                      </CardDescription>
                    </div>
                  </div>
                  <Badge className="bg-green-500 text-white text-sm px-3 py-1">
                    {subscription.billing_cycle === "monthly" ? "Monthly" : "Yearly"}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 gap-6">
                  {/* Plan Details */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-900">Plan Details</h4>
                    
                    <div className="flex items-center gap-3 text-slate-700">
                      <Users className="w-5 h-5 text-indigo-500" />
                      <span>
                        {subscription.driver_limit === -1 ? "Unlimited" : subscription.driver_limit} Drivers
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 text-slate-700">
                      <Car className="w-5 h-5 text-indigo-500" />
                      <span>
                        {subscription.rides_per_month === -1 ? "Unlimited" : subscription.rides_per_month} Rides/Month
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-3 text-slate-700">
                      <CreditCard className="w-5 h-5 text-indigo-500" />
                      <span className="text-2xl font-bold text-slate-900">
                        ${subscription.price}
                        <span className="text-sm font-normal text-slate-500">
                          /{subscription.billing_cycle === "monthly" ? "mo" : "yr"}
                        </span>
                      </span>
                    </div>
                  </div>

                  {/* Billing Info */}
                  <div className="space-y-4">
                    <h4 className="font-semibold text-slate-900">Billing Information</h4>
                    
                    <div className="flex items-center gap-3 text-slate-700">
                      <Calendar className="w-5 h-5 text-indigo-500" />
                      <div>
                        <p className="text-sm text-slate-500">Started</p>
                        <p className="font-medium">{formatDate(subscription.started_at)}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3 text-slate-700">
                      <RefreshCw className="w-5 h-5 text-indigo-500" />
                      <div>
                        <p className="text-sm text-slate-500">Next Billing Date</p>
                        <p className="font-medium">{formatDate(subscription.next_billing_date)}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Usage Stats */}
                <div className="mt-6 p-4 bg-white rounded-lg border border-green-200">
                  <h4 className="font-semibold text-slate-900 mb-3">This Month's Usage</h4>
                  <div className="flex items-center gap-4">
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-slate-600">Rides Used</span>
                        <span className="font-medium">
                          {subscription.rides_used_this_month || 0} / {subscription.rides_per_month === -1 ? "∞" : subscription.rides_per_month}
                        </span>
                      </div>
                      <div className="w-full bg-slate-200 rounded-full h-2">
                        <div 
                          className="bg-indigo-500 h-2 rounded-full transition-all"
                          style={{ 
                            width: subscription.rides_per_month === -1 
                              ? "10%" 
                              : `${Math.min((subscription.rides_used_this_month || 0) / subscription.rides_per_month * 100, 100)}%` 
                          }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => navigate("/pricing")}
                data-testid="change-plan-btn"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Change Plan
              </Button>
              
              <Button
                variant="outline"
                className="flex-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                onClick={handleCancelSubscription}
                disabled={isCancelling}
                data-testid="cancel-subscription-btn"
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XCircle className="w-4 h-4 mr-2" />
                    Cancel Subscription
                  </>
                )}
              </Button>
            </div>

            {/* Warning */}
            <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-amber-800">Cancellation Notice</p>
                <p className="text-sm text-amber-700">
                  If you cancel your subscription, you will lose access to the Dispatch Center immediately. 
                  Your drivers and clients will not be affected, but you won't be able to manage rides until you resubscribe.
                </p>
              </div>
            </div>
          </>
        ) : (
          /* No Subscription */
          <Card className="border-slate-200" data-testid="no-subscription-card">
            <CardContent className="py-12 text-center">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold text-slate-900 mb-2">
                No Active Subscription
              </h3>
              <p className="text-slate-600 mb-6 max-w-md mx-auto">
                Subscribe to a plan to access the Dispatch Center and start managing your transportation business.
              </p>
              <Button
                className="bg-indigo-900 hover:bg-indigo-800"
                onClick={() => navigate("/pricing")}
                data-testid="subscribe-now-btn"
              >
                <Crown className="w-4 h-4 mr-2" />
                View Plans & Subscribe
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
